import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import WizardStepper from './WizardStepper';
import './AddPaymentPorterWizard.css';
import './OracleCloudPaymentPorterWizard.css';

const STEPS = [
    { num: 1, label: 'Account' },
    { num: 2, label: 'Login' },
    { num: 3, label: 'Invoices' },
    { num: 4, label: 'Pay' },
    { num: 5, label: 'Method' },
    { num: 6, label: 'Confirm' },
];

const PORTER_ROUTE = '/apps/payment-porter/oracle-cloud/new';
const ORACLE_SIGNUP_URL = 'https://signup.oracle.com/';

// OAuth base must default to a sandbox host. The string literal "sandbox" is
// asserted in oracle-cloud-payment.spec.js — flipping this to a prod host
// must fail the test, not ship to users.
const ORACLE_OAUTH_BASE =
    process.env.REACT_APP_ORACLE_OAUTH_URL
    || 'https://login.sandbox.oraclecloud.example/oauth2/v1';

const OAUTH_CLIENT_ID = process.env.REACT_APP_ORACLE_OAUTH_CLIENT_ID || 'hconnect-payment-porter';

// sessionStorage keys for surviving the OAuth round-trip
const SS_OAUTH_KEY  = 'oracle-cloud-oauth';
const LS_TOKEN_PFX  = 'oracle-cloud-token:';

const ACCOUNT_ID_PATTERN = /^[A-Za-z0-9._-]{6,100}$/;

// Demo invoice dataset. Until the Step 3 lambda is built we render this
// directly. Tests can override the list at runtime by setting
// window.__oracleInvoices (or window.__oracleInvoicesError to force the
// error state); see oracle-cloud-payment.spec.js.
const SITE_NAMES = Array.from({ length: 13 }, (_, i) => `Site ${String(i + 1).padStart(2, '0')}`);
const INVOICE_PAGE_SIZE = 10;

function buildDemoInvoices() {
    const out = [];
    let n = 0;
    for (const site of SITE_NAMES) {
        // Two open invoices per site + one already-paid, so the dataset
        // exercises both Pay-enabled and Pay-disabled rows.
        for (const status of ['OPEN', 'OPEN', 'PAID']) {
            n += 1;
            out.push({
                id:       `inv-${String(n).padStart(4, '0')}`,
                number:   `OC-${(20260000 + n).toString()}`,
                site,
                issued:   `2026-${String(((n - 1) % 6) + 1).padStart(2, '0')}-15`,
                due:      `2026-${String(((n - 1) % 6) + 2).padStart(2, '0')}-15`,
                amount:   Number((1250 + (n * 37.5)).toFixed(2)),
                currency: 'USD',
                status,
            });
        }
    }
    return out;
}

function fmtAmount(amount, currency) {
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
    } catch {
        return `${currency} ${amount.toFixed(2)}`;
    }
}

function buildOAuthUrl({ accountId, csrfState, redirectUri }) {
    const params = new URLSearchParams({
        client_id:     OAUTH_CLIENT_ID,
        redirect_uri:  redirectUri,
        response_type: 'code',
        scope:         'invoices.read payments.write',
        state:         csrfState,
        login_hint:    accountId,
    });
    return `${ORACLE_OAUTH_BASE}/authorize?${params.toString()}`;
}

function generateState() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Spec rule: credit card payments are capped at US $100,000. Anything at or
// above this forces the user onto PayPal or bank transfer.
const CARD_AMOUNT_CAP_USD = 100000;

// PayPal must default to a sandbox host. Same pattern as ORACLE_OAUTH_BASE —
// the "sandbox" substring is asserted in the spec so a config flip can't
// silently point real users at production.
const PAYPAL_REDIRECT_BASE =
    process.env.REACT_APP_ORACLE_PAYPAL_URL
    || 'https://www.sandbox.paypal.com/checkoutnow';

function luhnOk(numStr) {
    const digits = (numStr || '').replace(/\D/g, '');
    if (digits.length < 12 || digits.length > 19) return false;
    let sum = 0;
    let alt = false;
    for (let i = digits.length - 1; i >= 0; i--) {
        let n = digits.charCodeAt(i) - 48;
        if (alt) { n *= 2; if (n > 9) n -= 9; }
        sum += n;
        alt = !alt;
    }
    return sum % 10 === 0;
}

function parseExpiry(mmYY) {
    const m = /^(\d{2})\s*\/\s*(\d{2})$/.exec((mmYY || '').trim());
    if (!m) return null;
    const month = Number(m[1]);
    const year  = 2000 + Number(m[2]);
    if (month < 1 || month > 12) return null;
    return { month, year };
}

function expiryIsFuture(parsed, now = new Date()) {
    if (!parsed) return false;
    // Cards expire at the END of the listed month, so include the month itself.
    const cmpYear  = now.getFullYear();
    const cmpMonth = now.getMonth() + 1;
    if (parsed.year < cmpYear) return false;
    if (parsed.year === cmpYear && parsed.month < cmpMonth) return false;
    return true;
}

// Permissive IBAN check: 2 letters + 2 digits + 11-30 alphanumeric chars.
// The full mod-97 check belongs in the Lambda, not the wizard.
const IBAN_PATTERN = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;

function normalisedIban(value) {
    return (value || '').replace(/\s+/g, '').toUpperCase();
}

const OracleCloudPaymentPorterWizard = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [step, setStep] = useState(1);
    const [hasAccount, setHasAccount] = useState(null);
    const [accountId, setAccountId] = useState('');
    const [errors, setErrors] = useState({});
    const [loginError, setLoginError] = useState(null);
    const [processingCallback, setProcessingCallback] = useState(false);
    // React StrictMode double-invokes mount effects in dev. Without this guard
    // the second run would see the cleared sessionStorage stash and treat the
    // (already-consumed) callback as a CSRF mismatch.
    const callbackHandled = useRef(false);

    // Step 3 — invoices
    const [invoices, setInvoices]               = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [invoicesError, setInvoicesError]     = useState(null);
    const [invoiceSearch, setInvoiceSearch]     = useState('');
    const [invoiceSiteFilter, setInvoiceSiteFilter] = useState('');
    const [invoicePage, setInvoicePage]         = useState(1);

    // Step 4 — selected invoice + duplicate-submission guard
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [continueInFlight, setContinueInFlight] = useState(false);
    const continueLockRef = useRef(false);

    // Step 5 — payment method
    const [paymentMethod, setPaymentMethod] = useState(null); // 'CARD' | 'PAYPAL' | 'DEBIT'
    const [cardForm, setCardForm] = useState({ number: '', expiry: '', cvv: '', name: '' });
    const [cardErrors, setCardErrors] = useState({});
    const [ibanValue, setIbanValue] = useState('');
    const [ibanError, setIbanError] = useState(null);
    const [submitInFlight, setSubmitInFlight] = useState(false);
    const submitLockRef = useRef(false);
    const [submissionError, setSubmissionError] = useState(null);
    // Step 6 placeholder: filled in by a successful submit so the receipt
    // card has the data it needs without a real Lambda yet.
    const [paymentReceipt, setPaymentReceipt] = useState(null);

    // Resets payment-method form state when leaving Step 5 in either direction.
    const resetPaymentMethodState = useCallback(() => {
        setPaymentMethod(null);
        setCardForm({ number: '', expiry: '', cvv: '', name: '' });
        setCardErrors({});
        setIbanValue('');
        setIbanError(null);
        setSubmissionError(null);
    }, []);

    // Returns null on success or an error message string. Real call goes
    // through the Lambda once it lands; for now we read window.__oraclePaymentResult
    // so tests can inject a DECLINE / IBY_FAILURE response.
    const submitPayment = useCallback(async ({ method, payload }) => {
        // Tiny artificial delay so the "Processing…" state is observable.
        await new Promise(r => setTimeout(r, 50));
        const injected = (typeof window !== 'undefined') ? window.__oraclePaymentResult : null;
        if (injected && injected.status === 'DECLINED') {
            const code = injected.failureCode || 'DECLINED';
            const reason = injected.reason ? `: ${injected.reason}` : '';
            return `Payment declined (${code})${reason}`;
        }
        // Anything else is treated as approved.
        const settledStatus = method === 'DEBIT' ? 'PROCESSING' : 'PAID';
        setPaymentReceipt({
            method,
            invoice:   selectedInvoice,
            payload,
            reference: `OC-PAY-${Date.now()}`,
            paidAt:    new Date().toISOString(),
            settledStatus,
        });
        // Flip the row in Step 3's list so it reflects the new status if
        // the user navigates back. DEBIT goes to PROCESSING (3-5 day clear),
        // CARD / PAYPAL go straight to PAID.
        setInvoices(prev => prev.map(inv => (
            inv.id === selectedInvoice?.id ? { ...inv, status: settledStatus } : inv
        )));
        return null;
    }, [selectedInvoice]);

    const validateCardForm = useCallback(() => {
        const errs = {};
        if (!luhnOk(cardForm.number)) errs.number = 'Card number is invalid';
        const parsed = parseExpiry(cardForm.expiry);
        if (!parsed) errs.expiry = 'Expiry must be MM/YY';
        else if (!expiryIsFuture(parsed)) errs.expiry = 'Card is expired';
        if (!/^\d{3,4}$/.test(cardForm.cvv)) errs.cvv = 'CVV must be 3-4 digits';
        if (!cardForm.name.trim()) errs.name = 'Name on card is required';
        return errs;
    }, [cardForm]);

    const startPaypalRedirect = useCallback(() => {
        if (!selectedInvoice) return;
        const params = new URLSearchParams({
            amount:   String(selectedInvoice.amount),
            currency: selectedInvoice.currency,
            invoice:  selectedInvoice.number,
            return:   `${window.location.origin}${PORTER_ROUTE}?paypal=success`,
            cancel:   `${window.location.origin}${PORTER_ROUTE}?paypal=cancel`,
        });
        const url = `${PAYPAL_REDIRECT_BASE}?${params.toString()}`;
        // Test seam — Chromium won't reliably let tests stub window.location.assign,
        // so honour an explicit hook when present (used by Playwright).
        if (typeof window.__oraclePaypalRedirect === 'function') {
            window.__oraclePaypalRedirect(url);
            return;
        }
        window.location.assign(url);
    }, [selectedInvoice]);

    const paypalHref = (() => {
        if (!selectedInvoice) return '#';
        const params = new URLSearchParams({
            amount:   String(selectedInvoice.amount),
            currency: selectedInvoice.currency,
            invoice:  selectedInvoice.number,
        });
        return `${PAYPAL_REDIRECT_BASE}?${params.toString()}`;
    })();

    const cardAllowed = Boolean(selectedInvoice && selectedInvoice.amount < CARD_AMOUNT_CAP_USD);

    const invoicesFetchedRef = useRef(false);

    const loadInvoices = useCallback(async () => {
        invoicesFetchedRef.current = true;
        setInvoicesLoading(true);
        setInvoicesError(null);
        try {
            // Test hooks: window.__oracleInvoicesError forces an error path
            // and window.__oracleInvoices replaces the dataset entirely.
            if (typeof window !== 'undefined' && window.__oracleInvoicesError) {
                throw new Error(window.__oracleInvoicesError);
            }
            const dataset = (typeof window !== 'undefined' && Array.isArray(window.__oracleInvoices))
                ? window.__oracleInvoices
                : buildDemoInvoices();
            // Tiny artificial delay so the loading state is visible in tests
            // and in the browser. Negligible on a real network call.
            await new Promise(r => setTimeout(r, 50));
            setInvoices(dataset);
        } catch (err) {
            setInvoicesError(err.message || 'Failed to load invoices');
            setInvoices([]);
        } finally {
            setInvoicesLoading(false);
        }
    }, []);

    useEffect(() => {
        // Only fetch on first entry to Step 3. Re-entering from Step 6
        // (after a successful payment) must preserve the in-memory list so
        // the user sees their just-paid invoice flipped to PAID.
        if (step === 3 && !invoicesFetchedRef.current) loadInvoices();
    }, [step, loadInvoices]);

    const siteOptions = useMemo(() => {
        const seen = new Set(invoices.map(i => i.site));
        return Array.from(seen).sort();
    }, [invoices]);

    const filteredInvoices = useMemo(() => {
        const q = invoiceSearch.trim().toLowerCase();
        return invoices.filter(inv => {
            if (invoiceSiteFilter && inv.site !== invoiceSiteFilter) return false;
            if (!q) return true;
            return (
                inv.number.toLowerCase().includes(q)
                || inv.site.toLowerCase().includes(q)
                || String(inv.amount).includes(q)
            );
        });
    }, [invoices, invoiceSearch, invoiceSiteFilter]);

    const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / INVOICE_PAGE_SIZE));
    const currentPage = Math.min(invoicePage, totalPages);
    const pagedInvoices = filteredInvoices.slice(
        (currentPage - 1) * INVOICE_PAGE_SIZE,
        currentPage * INVOICE_PAGE_SIZE,
    );

    // Reset to page 1 whenever the search or site filter changes.
    useEffect(() => { setInvoicePage(1); }, [invoiceSearch, invoiceSiteFilter]);

    // Handle the OAuth callback on mount. Oracle redirects back to this same
    // URL with ?code=...&state=... (success) or ?error=... (failure). The
    // accountId is lost across the round-trip so we restore it from
    // sessionStorage (set when the user clicked "Sign in").
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (!params.has('code') && !params.has('error')) return;
        if (callbackHandled.current) return;
        callbackHandled.current = true;

        const stashed = JSON.parse(sessionStorage.getItem(SS_OAUTH_KEY) || 'null');

        if (params.has('error')) {
            if (stashed?.accountId) {
                setAccountId(stashed.accountId);
                setHasAccount(true);
            }
            setStep(2);
            setLoginError(params.get('error_description') || params.get('error'));
            sessionStorage.removeItem(SS_OAUTH_KEY);
            navigate(PORTER_ROUTE, { replace: true });
            return;
        }

        // ?code path
        setProcessingCallback(true);
        const returnedState = params.get('state');
        if (!stashed || stashed.state !== returnedState) {
            setStep(2);
            setLoginError('Authentication state mismatch — please sign in again.');
            sessionStorage.removeItem(SS_OAUTH_KEY);
            setProcessingCallback(false);
            navigate(PORTER_ROUTE, { replace: true });
            return;
        }

        // Placeholder for the real /paymentporter/oracle/token exchange.
        // For now we mint a fake local token so the rest of the wizard can
        // proceed; the backend lambda will replace this in a later step.
        const token = {
            token:     `fake-${params.get('code')}`,
            accountId: stashed.accountId,
            issuedAt:  Date.now(),
            expiresIn: 3600,
        };
        localStorage.setItem(`${LS_TOKEN_PFX}${stashed.accountId}`, JSON.stringify(token));
        sessionStorage.removeItem(SS_OAUTH_KEY);

        setAccountId(stashed.accountId);
        setHasAccount(true);
        setStep(3);
        setProcessingCallback(false);
        navigate(PORTER_ROUTE, { replace: true });
    // Intentionally mount-only: the callbackHandled ref guards re-entry.
    }, []);

    const goToStep2 = () => {
        const trimmed = accountId.trim();
        if (!trimmed) {
            setErrors({ accountId: 'Account ID is required' });
            return;
        }
        if (!ACCOUNT_ID_PATTERN.test(trimmed)) {
            setErrors({ accountId: 'Account ID format is invalid' });
            return;
        }
        setErrors({});
        setAccountId(trimmed);
        setLoginError(null);
        setStep(2);
    };

    const signInToOracle = () => {
        const csrfState   = generateState();
        const redirectUri = `${window.location.origin}${PORTER_ROUTE}`;
        sessionStorage.setItem(SS_OAUTH_KEY, JSON.stringify({ state: csrfState, accountId }));
        window.location.assign(buildOAuthUrl({ accountId, csrfState, redirectUri }));
    };

    const oauthHref = (() => {
        // We render the sign-in CTA as an anchor so tests can assert on the
        // href without ever navigating. The real click handler still runs
        // signInToOracle() to capture the CSRF state.
        if (step !== 2 || !accountId) return '#';
        const csrfPreview = 'preview'; // overwritten by signInToOracle on click
        const redirectUri = (typeof window !== 'undefined')
            ? `${window.location.origin}${PORTER_ROUTE}`
            : PORTER_ROUTE;
        return buildOAuthUrl({ accountId, csrfState: csrfPreview, redirectUri });
    })();

    return (
        <div className="ppw-page" data-testid="oracle-cloud-porter">
            <WizardStepper steps={STEPS} currentStep={step} />

            {step === 1 && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Connect your Oracle Cloud account</h2>
                        <p className="ppw-card-subtitle">
                            Pay Oracle Cloud invoices for your hotels through hconnect.
                        </p>
                    </div>

                    <div className="ppw-card-body">
                        <div className="ppw-field">
                            <label className="ppw-label">
                                Do you have an Oracle Cloud account? <span className="ppw-required">*</span>
                            </label>
                            <div className="ppw-radio-group" role="radiogroup" aria-label="Oracle Cloud account">
                                <label className="ppw-radio-label">
                                    <input
                                        type="radio"
                                        name="hasAccount"
                                        value="yes"
                                        checked={hasAccount === true}
                                        onChange={() => { setHasAccount(true); setErrors({}); }}
                                    />
                                    Yes, I have an account
                                </label>
                                <label className="ppw-radio-label">
                                    <input
                                        type="radio"
                                        name="hasAccount"
                                        value="no"
                                        checked={hasAccount === false}
                                        onChange={() => { setHasAccount(false); setErrors({}); }}
                                    />
                                    No, sign me up
                                </label>
                            </div>
                        </div>

                        {hasAccount === true && (
                            <div className="ppw-field">
                                <label className="ppw-label" htmlFor="oracle-account-id">
                                    Oracle Cloud account ID <span className="ppw-required">*</span>
                                </label>
                                <input
                                    id="oracle-account-id"
                                    name="accountId"
                                    className={`ppw-input${errors.accountId ? ' ppw-input-error' : ''}`}
                                    type="text"
                                    placeholder="e.g., ocid1.tenancy.oc1..aaaaaaaa..."
                                    value={accountId}
                                    onChange={e => {
                                        setAccountId(e.target.value);
                                        if (errors.accountId) setErrors(v => ({ ...v, accountId: undefined }));
                                    }}
                                    autoFocus
                                />
                                {errors.accountId && (
                                    <span className="ppw-error-msg">{errors.accountId}</span>
                                )}
                            </div>
                        )}

                        {hasAccount === false && (
                            <div className="ppw-field ppw-signup-cta">
                                <p>
                                    You'll need an Oracle Cloud account before continuing. Sign up with
                                    Oracle in a new tab, then come back here to finish setup.
                                </p>
                                <a
                                    className="ppw-btn-link"
                                    href={ORACLE_SIGNUP_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="oracle-signup-link"
                                >
                                    Open Oracle signup ↗
                                </a>
                            </div>
                        )}
                    </div>

                    <div className="ppw-footer">
                        <button
                            className="ppw-btn-cancel"
                            onClick={() => navigate('/apps/payment-porter')}
                        >
                            Cancel
                        </button>
                        <button
                            className="ppw-btn-next"
                            onClick={goToStep2}
                            disabled={hasAccount !== true}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="ppw-card">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Sign in to Oracle Cloud</h2>
                        <p className="ppw-card-subtitle">
                            We'll redirect you to Oracle to authorise hconnect to read invoices
                            and submit payments for this account.
                        </p>
                    </div>

                    <div className="ppw-card-body">
                        {processingCallback && (
                            <div className="ppw-field" data-testid="oracle-oauth-processing">
                                <p>Completing sign-in…</p>
                            </div>
                        )}

                        {!processingCallback && (
                            <>
                                <div className="ppw-field">
                                    <label className="ppw-label">Account</label>
                                    <p className="ppw-readonly-value" data-testid="oracle-step2-account-id">
                                        {accountId}
                                    </p>
                                </div>

                                {loginError && (
                                    <div className="ppw-error-banner" role="alert" data-testid="oracle-login-error">
                                        Sign-in failed: {loginError}
                                    </div>
                                )}

                                <div className="ppw-field">
                                    <a
                                        className="ppw-btn-next ppw-btn-oauth"
                                        href={oauthHref}
                                        onClick={e => {
                                            // Pre-empt the default href (which carries the placeholder
                                            // CSRF) and navigate with a fresh state instead.
                                            e.preventDefault();
                                            signInToOracle();
                                        }}
                                        data-testid="oracle-oauth-signin"
                                    >
                                        Sign in to Oracle Cloud ↗
                                    </a>
                                    <p className="ppw-oauth-target">
                                        Redirects to <code data-testid="oracle-oauth-host">{ORACLE_OAUTH_BASE}</code>
                                    </p>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="ppw-footer">
                        <button
                            className="ppw-btn-cancel"
                            onClick={() => { setLoginError(null); setStep(1); }}
                            disabled={processingCallback}
                        >
                            ← Back
                        </button>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className="ppw-card" data-testid="oracle-step3">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Oracle Cloud invoices</h2>
                        <p className="ppw-card-subtitle">
                            Signed in as <strong>{accountId}</strong>. Choose an invoice to pay.
                        </p>
                    </div>

                    <div className="ppw-card-body">
                        <div className="ppw-invoice-toolbar">
                            <input
                                type="search"
                                className="ppw-input ppw-invoice-search"
                                placeholder="Search by invoice #, site or amount"
                                value={invoiceSearch}
                                onChange={e => setInvoiceSearch(e.target.value)}
                                data-testid="oracle-invoice-search"
                            />
                            <select
                                className="ppw-input ppw-invoice-site-filter"
                                value={invoiceSiteFilter}
                                onChange={e => setInvoiceSiteFilter(e.target.value)}
                                data-testid="oracle-invoice-site-filter"
                                aria-label="Filter by site"
                            >
                                <option value="">All sites</option>
                                {siteOptions.map(site => (
                                    <option key={site} value={site}>{site}</option>
                                ))}
                            </select>
                        </div>

                        {invoicesLoading && (
                            <p className="ppw-invoice-status" data-testid="oracle-invoices-loading">
                                Loading invoices…
                            </p>
                        )}

                        {invoicesError && !invoicesLoading && (
                            <div className="ppw-error-banner" role="alert" data-testid="oracle-invoices-error">
                                Couldn't reach Oracle Cloud: {invoicesError}
                                <button
                                    className="ppw-btn-link ppw-invoice-retry"
                                    onClick={loadInvoices}
                                    data-testid="oracle-invoices-retry"
                                >
                                    Retry
                                </button>
                            </div>
                        )}

                        {!invoicesLoading && !invoicesError && filteredInvoices.length === 0 && (
                            <p className="ppw-invoice-empty" data-testid="oracle-invoices-empty">
                                {invoices.length === 0
                                    ? 'No open invoices for this account.'
                                    : 'No invoices match your filters.'}
                            </p>
                        )}

                        {!invoicesLoading && !invoicesError && pagedInvoices.length > 0 && (
                            <>
                                <table className="ppw-invoice-table" data-testid="oracle-invoice-table">
                                    <thead>
                                        <tr>
                                            <th>Invoice #</th>
                                            <th>Site</th>
                                            <th>Issued</th>
                                            <th>Due</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th aria-label="Pay" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagedInvoices.map(inv => (
                                            <tr key={inv.id} data-testid={`oracle-invoice-row-${inv.id}`}>
                                                <td>{inv.number}</td>
                                                <td>{inv.site}</td>
                                                <td>{inv.issued}</td>
                                                <td>{inv.due}</td>
                                                <td className="ppw-invoice-amount">
                                                    {fmtAmount(inv.amount, inv.currency)}
                                                </td>
                                                <td>
                                                    <span className={`ppw-invoice-status-pill ppw-invoice-status-${inv.status.toLowerCase()}`}>
                                                        {inv.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className="ppw-btn-next ppw-invoice-pay"
                                                        disabled={inv.status !== 'OPEN'}
                                                        data-testid={`oracle-invoice-pay-${inv.id}`}
                                                        onClick={() => {
                                                            if (inv.status !== 'OPEN') return;
                                                            setSelectedInvoice(inv);
                                                            setStep(4);
                                                        }}
                                                    >
                                                        Pay
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {totalPages > 1 && (
                                    <div className="ppw-invoice-pager" data-testid="oracle-invoice-pager">
                                        <button
                                            className="ppw-btn-cancel"
                                            onClick={() => setInvoicePage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            data-testid="oracle-invoice-pager-prev"
                                        >
                                            ← Prev
                                        </button>
                                        <span className="ppw-invoice-pager-state" data-testid="oracle-invoice-pager-state">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            className="ppw-btn-next"
                                            onClick={() => setInvoicePage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            data-testid="oracle-invoice-pager-next"
                                        >
                                            Next →
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="ppw-footer">
                        <button
                            className="ppw-btn-cancel"
                            onClick={() => setStep(2)}
                        >
                            ← Back
                        </button>
                        <button
                            className="ppw-btn-cancel"
                            onClick={loadInvoices}
                            disabled={invoicesLoading}
                            data-testid="oracle-invoices-refresh"
                        >
                            Refresh
                        </button>
                    </div>
                </div>
            )}

            {step === 4 && selectedInvoice && (
                <div className="ppw-card" data-testid="oracle-step4">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Confirm invoice to pay</h2>
                        <p className="ppw-card-subtitle">
                            Check the details below before choosing a payment method.
                        </p>
                    </div>

                    <div className="ppw-card-body">
                        <dl className="ppw-invoice-detail">
                            <div className="ppw-invoice-detail-row">
                                <dt>Invoice #</dt>
                                <dd data-testid="oracle-step4-number">{selectedInvoice.number}</dd>
                            </div>
                            <div className="ppw-invoice-detail-row">
                                <dt>Site</dt>
                                <dd data-testid="oracle-step4-site">{selectedInvoice.site}</dd>
                            </div>
                            <div className="ppw-invoice-detail-row">
                                <dt>Issued</dt>
                                <dd>{selectedInvoice.issued}</dd>
                            </div>
                            <div className="ppw-invoice-detail-row">
                                <dt>Due</dt>
                                <dd>{selectedInvoice.due}</dd>
                            </div>
                            <div className="ppw-invoice-detail-row">
                                <dt>Status</dt>
                                <dd>
                                    <span className={`ppw-invoice-status-pill ppw-invoice-status-${selectedInvoice.status.toLowerCase()}`}>
                                        {selectedInvoice.status}
                                    </span>
                                </dd>
                            </div>
                            <div className="ppw-invoice-detail-row ppw-invoice-detail-amount">
                                <dt>Amount</dt>
                                <dd>
                                    <input
                                        className="ppw-input ppw-invoice-amount-input"
                                        type="text"
                                        readOnly
                                        value={fmtAmount(selectedInvoice.amount, selectedInvoice.currency)}
                                        data-testid="oracle-step4-amount"
                                        aria-label="Invoice amount"
                                    />
                                </dd>
                            </div>
                        </dl>

                        {selectedInvoice.status !== 'OPEN' && (
                            <div className="ppw-error-banner" role="alert" data-testid="oracle-step4-already-paid">
                                This invoice is already {selectedInvoice.status.toLowerCase()}. Return to the
                                invoice list to choose another.
                            </div>
                        )}
                    </div>

                    <div className="ppw-footer">
                        <button
                            className="ppw-btn-cancel"
                            onClick={() => { setSelectedInvoice(null); setStep(3); }}
                            disabled={continueInFlight}
                            data-testid="oracle-step4-back"
                        >
                            ← Back
                        </button>
                        <button
                            className="ppw-btn-next"
                            disabled={selectedInvoice.status !== 'OPEN' || continueInFlight}
                            data-testid="oracle-step4-continue"
                            onClick={() => {
                                // Synchronous guard so a double-click within the
                                // same tick can't enqueue two transitions before
                                // React schedules the state update.
                                if (continueLockRef.current) return;
                                if (selectedInvoice.status !== 'OPEN') return;
                                continueLockRef.current = true;
                                setContinueInFlight(true);
                                setStep(5);
                                // Release the lock on the next tick — by then
                                // Step 5 is mounted and the user can't see
                                // Step 4's button to double-click it.
                                setTimeout(() => {
                                    continueLockRef.current = false;
                                    setContinueInFlight(false);
                                }, 0);
                            }}
                        >
                            {continueInFlight ? 'Processing…' : 'Choose payment method →'}
                        </button>
                    </div>
                </div>
            )}

            {step === 5 && selectedInvoice && (
                <div className="ppw-card" data-testid="oracle-step5">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Choose a payment method</h2>
                        <p className="ppw-card-subtitle">
                            Paying <strong>{fmtAmount(selectedInvoice.amount, selectedInvoice.currency)}</strong>
                            {' '}for <strong>{selectedInvoice.number}</strong>.
                        </p>
                    </div>

                    <div className="ppw-card-body">
                        <div className="ppw-method-grid" role="radiogroup" aria-label="Payment method">
                            <label className={`ppw-method-card${paymentMethod === 'CARD' ? ' selected' : ''}${!cardAllowed ? ' disabled' : ''}`}>
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    value="CARD"
                                    disabled={!cardAllowed}
                                    checked={paymentMethod === 'CARD'}
                                    onChange={() => { setPaymentMethod('CARD'); setSubmissionError(null); }}
                                    data-testid="oracle-method-card"
                                />
                                <div className="ppw-method-card-body">
                                    <div className="ppw-method-title">Credit card</div>
                                    <div className="ppw-method-sub">
                                        {cardAllowed
                                            ? 'Charged immediately'
                                            : `Not available for invoices over ${fmtAmount(CARD_AMOUNT_CAP_USD, 'USD')}`}
                                    </div>
                                </div>
                            </label>

                            <label className={`ppw-method-card${paymentMethod === 'PAYPAL' ? ' selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    value="PAYPAL"
                                    checked={paymentMethod === 'PAYPAL'}
                                    onChange={() => { setPaymentMethod('PAYPAL'); setSubmissionError(null); }}
                                    data-testid="oracle-method-paypal"
                                />
                                <div className="ppw-method-card-body">
                                    <div className="ppw-method-title">PayPal</div>
                                    <div className="ppw-method-sub">Redirects to PayPal, then back here</div>
                                </div>
                            </label>

                            <label className={`ppw-method-card${paymentMethod === 'DEBIT' ? ' selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    value="DEBIT"
                                    checked={paymentMethod === 'DEBIT'}
                                    onChange={() => { setPaymentMethod('DEBIT'); setSubmissionError(null); }}
                                    data-testid="oracle-method-debit"
                                />
                                <div className="ppw-method-card-body">
                                    <div className="ppw-method-title">Direct debit</div>
                                    <div className="ppw-method-sub">Bank transfer, 3-5 business days</div>
                                </div>
                            </label>
                        </div>

                        {paymentMethod === 'CARD' && (
                            <div className="ppw-method-form" data-testid="oracle-card-form">
                                <div className="ppw-field">
                                    <label className="ppw-label" htmlFor="oracle-card-number">
                                        Card number <span className="ppw-required">*</span>
                                    </label>
                                    <input
                                        id="oracle-card-number"
                                        className={`ppw-input${cardErrors.number ? ' ppw-input-error' : ''}`}
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="cc-number"
                                        placeholder="4242 4242 4242 4242"
                                        value={cardForm.number}
                                        onChange={e => {
                                            setCardForm(f => ({ ...f, number: e.target.value }));
                                            if (cardErrors.number) setCardErrors(v => ({ ...v, number: undefined }));
                                        }}
                                        data-testid="oracle-card-number"
                                    />
                                    {cardErrors.number && (
                                        <span className="ppw-error-msg" data-testid="oracle-card-number-error">
                                            {cardErrors.number}
                                        </span>
                                    )}
                                </div>
                                <div className="ppw-row-2">
                                    <div className="ppw-field">
                                        <label className="ppw-label" htmlFor="oracle-card-expiry">
                                            Expiry <span className="ppw-required">*</span>
                                        </label>
                                        <input
                                            id="oracle-card-expiry"
                                            className={`ppw-input${cardErrors.expiry ? ' ppw-input-error' : ''}`}
                                            type="text"
                                            placeholder="MM/YY"
                                            value={cardForm.expiry}
                                            onChange={e => {
                                                setCardForm(f => ({ ...f, expiry: e.target.value }));
                                                if (cardErrors.expiry) setCardErrors(v => ({ ...v, expiry: undefined }));
                                            }}
                                            data-testid="oracle-card-expiry"
                                        />
                                        {cardErrors.expiry && (
                                            <span className="ppw-error-msg" data-testid="oracle-card-expiry-error">
                                                {cardErrors.expiry}
                                            </span>
                                        )}
                                    </div>
                                    <div className="ppw-field">
                                        <label className="ppw-label" htmlFor="oracle-card-cvv">
                                            CVV <span className="ppw-required">*</span>
                                        </label>
                                        <input
                                            id="oracle-card-cvv"
                                            className={`ppw-input${cardErrors.cvv ? ' ppw-input-error' : ''}`}
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="123"
                                            value={cardForm.cvv}
                                            onChange={e => {
                                                setCardForm(f => ({ ...f, cvv: e.target.value }));
                                                if (cardErrors.cvv) setCardErrors(v => ({ ...v, cvv: undefined }));
                                            }}
                                            data-testid="oracle-card-cvv"
                                        />
                                        {cardErrors.cvv && (
                                            <span className="ppw-error-msg" data-testid="oracle-card-cvv-error">
                                                {cardErrors.cvv}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="ppw-field">
                                    <label className="ppw-label" htmlFor="oracle-card-name">
                                        Name on card <span className="ppw-required">*</span>
                                    </label>
                                    <input
                                        id="oracle-card-name"
                                        className={`ppw-input${cardErrors.name ? ' ppw-input-error' : ''}`}
                                        type="text"
                                        autoComplete="cc-name"
                                        value={cardForm.name}
                                        onChange={e => {
                                            setCardForm(f => ({ ...f, name: e.target.value }));
                                            if (cardErrors.name) setCardErrors(v => ({ ...v, name: undefined }));
                                        }}
                                        data-testid="oracle-card-name"
                                    />
                                    {cardErrors.name && (
                                        <span className="ppw-error-msg" data-testid="oracle-card-name-error">
                                            {cardErrors.name}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {paymentMethod === 'PAYPAL' && (
                            <div className="ppw-method-form" data-testid="oracle-paypal-form">
                                <p>You'll be redirected to PayPal to complete the payment.</p>
                                <p className="ppw-oauth-target">
                                    Redirects to <code data-testid="oracle-paypal-host">{PAYPAL_REDIRECT_BASE}</code>
                                </p>
                                <a
                                    className="ppw-btn-link"
                                    href={paypalHref}
                                    onClick={e => e.preventDefault()}
                                    data-testid="oracle-paypal-href"
                                >
                                    Preview redirect URL
                                </a>
                            </div>
                        )}

                        {paymentMethod === 'DEBIT' && (
                            <div className="ppw-method-form" data-testid="oracle-debit-form">
                                <div className="ppw-field">
                                    <label className="ppw-label" htmlFor="oracle-debit-iban">
                                        IBAN <span className="ppw-required">*</span>
                                    </label>
                                    <input
                                        id="oracle-debit-iban"
                                        className={`ppw-input${ibanError ? ' ppw-input-error' : ''}`}
                                        type="text"
                                        autoComplete="off"
                                        placeholder="NL91 ABNA 0417 1643 00"
                                        value={ibanValue}
                                        onChange={e => {
                                            setIbanValue(e.target.value);
                                            if (ibanError) setIbanError(null);
                                        }}
                                        data-testid="oracle-debit-iban"
                                    />
                                    {ibanError && (
                                        <span className="ppw-error-msg" data-testid="oracle-debit-iban-error">
                                            {ibanError}
                                        </span>
                                    )}
                                </div>
                                <p className="ppw-debit-notice" data-testid="oracle-debit-timing">
                                    Funds will clear in <strong>3-5 business days</strong>. The invoice stays open
                                    until the transfer settles.
                                </p>
                            </div>
                        )}

                        {submissionError && (
                            <div className="ppw-error-banner" role="alert" data-testid="oracle-step5-submit-error">
                                {submissionError}
                            </div>
                        )}
                    </div>

                    <div className="ppw-footer">
                        <button
                            className="ppw-btn-cancel"
                            onClick={() => { resetPaymentMethodState(); setStep(4); }}
                            disabled={submitInFlight}
                            data-testid="oracle-step5-back"
                        >
                            ← Back
                        </button>
                        <button
                            className="ppw-btn-next"
                            disabled={!paymentMethod || submitInFlight}
                            data-testid="oracle-step5-submit"
                            onClick={async () => {
                                if (submitLockRef.current) return;
                                if (!paymentMethod) return;

                                if (paymentMethod === 'CARD') {
                                    if (!cardAllowed) return;
                                    const errs = validateCardForm();
                                    if (Object.keys(errs).length) { setCardErrors(errs); return; }
                                }
                                if (paymentMethod === 'DEBIT') {
                                    const iban = normalisedIban(ibanValue);
                                    if (!IBAN_PATTERN.test(iban)) {
                                        setIbanError('IBAN format is invalid');
                                        return;
                                    }
                                }
                                if (paymentMethod === 'PAYPAL') {
                                    submitLockRef.current = true;
                                    setSubmitInFlight(true);
                                    startPaypalRedirect();
                                    // The redirect navigates the browser away; if it
                                    // doesn't fire in tests (window.location.assign
                                    // is intercepted) we release the lock so the
                                    // tester can keep clicking.
                                    setTimeout(() => {
                                        submitLockRef.current = false;
                                        setSubmitInFlight(false);
                                    }, 200);
                                    return;
                                }

                                submitLockRef.current = true;
                                setSubmitInFlight(true);
                                setSubmissionError(null);
                                let payload = null;
                                if (paymentMethod === 'CARD') {
                                    payload = {
                                        last4:    cardForm.number.replace(/\D/g, '').slice(-4),
                                        expiry:   cardForm.expiry,
                                        nameOnCard: cardForm.name,
                                    };
                                } else if (paymentMethod === 'DEBIT') {
                                    payload = { iban: normalisedIban(ibanValue) };
                                }
                                const err = await submitPayment({ method: paymentMethod, payload });
                                if (err) {
                                    setSubmissionError(err);
                                    submitLockRef.current = false;
                                    setSubmitInFlight(false);
                                    return;
                                }
                                submitLockRef.current = false;
                                setSubmitInFlight(false);
                                setStep(6);
                            }}
                        >
                            {submitInFlight ? 'Processing…'
                                : paymentMethod === 'PAYPAL' ? 'Continue to PayPal →'
                                : paymentMethod === 'DEBIT'  ? 'Confirm transfer →'
                                : 'Pay'}
                        </button>
                    </div>
                </div>
            )}

            {step === 6 && paymentReceipt && (
                <div className="ppw-card" data-testid="oracle-step6">
                    <div className="ppw-card-header">
                        <h2 className="ppw-card-title">Payment {paymentReceipt.settledStatus === 'PROCESSING' ? 'submitted' : 'complete'}</h2>
                        <p className="ppw-card-subtitle">
                            {paymentReceipt.settledStatus === 'PROCESSING'
                                ? 'Funds will clear in 3-5 business days. We\'ll update this invoice when settlement is confirmed.'
                                : 'Thanks. Your payment has gone through. A receipt is below.'}
                        </p>
                    </div>

                    <div className="ppw-card-body">
                        <dl className="ppw-invoice-detail">
                            <div className="ppw-invoice-detail-row">
                                <dt>Reference</dt>
                                <dd data-testid="oracle-step6-reference">{paymentReceipt.reference}</dd>
                            </div>
                            <div className="ppw-invoice-detail-row">
                                <dt>Invoice #</dt>
                                <dd data-testid="oracle-step6-invoice">{paymentReceipt.invoice?.number}</dd>
                            </div>
                            <div className="ppw-invoice-detail-row">
                                <dt>Site</dt>
                                <dd data-testid="oracle-step6-site">{paymentReceipt.invoice?.site}</dd>
                            </div>
                            <div className="ppw-invoice-detail-row">
                                <dt>Amount</dt>
                                <dd data-testid="oracle-step6-amount">
                                    {fmtAmount(paymentReceipt.invoice?.amount, paymentReceipt.invoice?.currency)}
                                </dd>
                            </div>
                            <div className="ppw-invoice-detail-row">
                                <dt>Method</dt>
                                <dd data-testid="oracle-step6-method">
                                    {paymentReceipt.method === 'CARD'   ? 'Credit card'
                                        : paymentReceipt.method === 'PAYPAL' ? 'PayPal'
                                            : paymentReceipt.method === 'DEBIT'  ? 'Direct debit'
                                                : paymentReceipt.method}
                                    {paymentReceipt.method === 'CARD' && paymentReceipt.payload?.last4
                                        ? ` •••• ${paymentReceipt.payload.last4}` : ''}
                                </dd>
                            </div>
                            <div className="ppw-invoice-detail-row">
                                <dt>Status</dt>
                                <dd>
                                    <span
                                        className={`ppw-invoice-status-pill ppw-invoice-status-${paymentReceipt.settledStatus.toLowerCase()}`}
                                        data-testid="oracle-step6-status"
                                    >
                                        {paymentReceipt.settledStatus}
                                    </span>
                                </dd>
                            </div>
                            <div className="ppw-invoice-detail-row">
                                <dt>Paid at</dt>
                                <dd data-testid="oracle-step6-paidat">{paymentReceipt.paidAt}</dd>
                            </div>
                        </dl>
                    </div>

                    <div className="ppw-footer">
                        <button
                            className="ppw-btn-cancel"
                            onClick={() => window.print()}
                            data-testid="oracle-step6-download"
                        >
                            Download receipt
                        </button>
                        <button
                            className="ppw-btn-cancel"
                            onClick={() => {
                                // Return to the invoice list (Step 3). The
                                // invoice we just paid is already updated in
                                // local state.
                                resetPaymentMethodState();
                                setPaymentReceipt(null);
                                setSelectedInvoice(null);
                                setStep(3);
                            }}
                            data-testid="oracle-step6-view-invoices"
                        >
                            View invoices
                        </button>
                        <button
                            className="ppw-btn-next"
                            onClick={() => navigate('/apps/payment-porter')}
                            data-testid="oracle-step6-done"
                        >
                            Done
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OracleCloudPaymentPorterWizard;
